import * as React from 'react';
import { datadogRum } from '@datadog/browser-rum-slim';
import { Button, useToast } from '@seccl/ui';
import Router, { useRouter } from 'next/router';

import { isDifferentBuildLive, isFailedDataNavigation } from '../utils/staleBuildNavigation';

import styles from './useStaleBuildPrompt.module.scss';

const PROMPT_MESSAGE =
  "A newer version has been released, so the page you tried to open didn't load. Please finish and submit anything you're working on, then refresh.";

const fetchServerVersion = async (): Promise<string | undefined> => {
  try {
    const response = await fetch('/api/env', { cache: 'no-store' });
    if (!response.ok) {
      return undefined;
    }
    const { version } = await response.json();
    return typeof version === 'string' ? version : undefined;
  } catch {
    return undefined;
  }
};

/**
 * When a click on the page you are already on fails to load its data, Next gives up without a message. If the server
 * is running a different build, this tells the user and offers a refresh. Anything else behaves as it did before.
 */
export const useStaleBuildPrompt = () => {
  const router = useRouter();
  const { dispatchToast } = useToast();
  const dispatchToastRef = React.useRef(dispatchToast);
  const isMountedRef = React.useRef(false);
  const isCheckingRef = React.useRef(false);
  const promptedVersionRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    dispatchToastRef.current = dispatchToast;
  }, [dispatchToast]);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    const handleRouteChangeError = async (error: Error, targetUrl: string) => {
      const shouldCheckServer = !isCheckingRef.current && isFailedDataNavigation(error, targetUrl, router.asPath);
      if (!shouldCheckServer) {
        return;
      }

      isCheckingRef.current = true;
      const serverVersion = await fetchServerVersion().finally(() => {
        isCheckingRef.current = false;
      });
      const clientVersion = process.env.NEXT_PUBLIC_PRO_VERSION ?? 'local';
      const isStillOnFailedPage = Router.asPath === targetUrl;
      const shouldPrompt =
        isMountedRef.current &&
        isStillOnFailedPage &&
        isDifferentBuildLive(serverVersion, clientVersion) &&
        promptedVersionRef.current !== serverVersion;
      if (!shouldPrompt) {
        return;
      }

      promptedVersionRef.current = serverVersion;
      const versions = { clientVersion, serverVersion };
      datadogRum.addAction('stale_build_prompt_shown', versions);
      dispatchToastRef.current({
        title: 'This page needs refreshing',
        message: <div className={styles.message}>{PROMPT_MESSAGE}</div>,
        variant: 'info',
        persist: true,
        action: () => (
          <Button
            className={styles.refresh_button}
            onClick={() => {
              datadogRum.addAction('stale_build_prompt_refresh_clicked', versions);
              window.location.reload();
            }}
          >
            Refresh page
          </Button>
        ),
      });
    };

    router.events.on('routeChangeError', handleRouteChangeError);
    return () => {
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router]);
};
